import React, { useEffect, useRef, useState } from 'react'; // useRef'i ekledik

import contact from '../assets/images/contact.svg';
import { useTranslation } from 'react-i18next';
import { FiCheckCircle, FiCircle } from 'react-icons/fi';

// Google Form URL'sini ve Alan ID'lerini bir nesnede saklamak daha okunaklı olur
const GOOGLE_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSd89MgXoMQLFdvJPY37Q9ZZizVMTA9Bbz__h_h16aZ5vX3g9A/formResponse';
// NOT: entry.xxx değerleri zaten form inputlarında tanımlı olduğu için
// bu örnekte direkt form elemanlarını kullanacağız, ek bir eşleme objesine gerek yok.

export default function Contact() {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  // Mesaj durumunu yönetmek için state
  const [formStatus, setFormStatus] = useState({
    message: '',
    type: '', // 'success', 'error' veya 'info'
    visible: false,
  });

  // Mesajı 5 saniye sonra otomatik gizlemek için useEffect
  useEffect(() => {
    if (formStatus.visible) {
      const timer = setTimeout(() => {
        setFormStatus({ message: '', type: '', visible: false });
      }, 5000); // 5 saniye
      return () => clearTimeout(timer);
    }
  }, [formStatus.visible]);

  const handleSubmit = (event: any) => {
    event.preventDefault(); // Varsayılan form gönderme işlemini durdur

    const currentForm = formRef.current;
    if (!currentForm) return;

    // Gönderim başladığında 'info' mesajını göster
    setFormStatus({ message: 'Gönderiliyor...', type: 'info', visible: true }); // Verileri doğrudan FormData nesnesi ile topla

    const formData = new FormData(currentForm); // POST İsteği Gönderme

    fetch(GOOGLE_FORM_URL, {
      method: 'POST',
      body: formData,
      mode: 'no-cors', // Google Formlar için gereklidir
    })
      .then(() => {
        // Başarı durumu için mesajı göster
        setFormStatus({
          message: 'Mesajınız başarıyla gönderildi. Teşekkürler!',
          type: 'success',
          visible: true,
        });

        if (formRef.current) {
          formRef.current.reset(); // Formu sıfırla
        }
      })
      .catch((error) => {
        // Hata durumu için mesajı göster
        console.error('Gönderme hatası:', error);
        setFormStatus({
          message: 'Gönderme sırasında bir sorun oluştu.',
          type: 'error',
          visible: true,
        });
      });
  };

  // Tailwind sınıflarını duruma göre belirleyen yardımcı fonksiyon
  const getStatusClasses = () => {
    switch (formStatus.type) {
      case 'success':
        return 'bg-green-100 border-green-400 text-green-700';
      case 'error':
        return 'bg-red-100 border-red-400 text-red-700';
      case 'info':
        return 'bg-blue-100 border-blue-400 text-blue-700';
      default:
        return 'hidden';
    }
  };

  const StatusIcon = formStatus.type === 'success' ? FiCheckCircle : FiCircle;

  return (
    <div className="container relative">
      <div className="grid md:grid-cols-12 grid-cols-1 items-center gap-[30px]">
        <div className="lg:col-span-7 md:col-span-6">
          <img src={contact} alt={t('contact.imageAlt')} />
        </div>

        <div className="lg:col-span-5 md:col-span-6">
          <div className="lg:ms-5">
            <div className="bg-white dark:bg-slate-900 rounded-md shadow dark:shadow-gray-800 p-6">
              <h6 className="text-red-500 uppercase text-sm font-bold tracking-wider mb-3">
                {t('contact.subtitle')}
              </h6>
              <h4 className="mb-6 md:text-3xl text-2xl md:leading-normal leading-normal font-bold">
                {t('contact.title')}
              </h4>

              <div
                className={`border px-4 py-3 rounded-lg mb-6 transition-all duration-300 ${
                  formStatus.visible ? 'opacity-100 max-h-40' : 'opacity-0 max-h-0 overflow-hidden'
                } ${getStatusClasses()}`}
                role="alert"
              >
                <div className="flex items-center">
                  {/* Yalnızca 'info' değilse ve görünürse ikon göster */}
                  {formStatus.type !== 'info' && formStatus.visible && (
                    <StatusIcon className="w-5 h-5 me-3" />
                  )}
                  <p className="font-semibold text-sm">{formStatus.message}</p>
                </div>
              </div>

              {/* Formun action/method özelliklerini kaldırdık ve onSubmit ekledik */}
              <form
                ref={formRef} // Forma erişmek için ref'i ekledik
                onSubmit={handleSubmit} // Yeni form gönderme işleyicisini ekledik
              >
                <div className="grid lg:grid-cols-12 gap-3">
                  <div className="lg:col-span-6">
                    <div className="text-start">
                      <label htmlFor="name" className="font-medium">
                        {t('contact.form.nameLabel')}
                      </label>
                      <input
                        name="entry.1878577922" // Google Form ID'si
                        id="name"
                        type="text"
                        className="form-input mt-1 w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 focus:border-red-500 dark:border-gray-800 dark:focus:border-red-500 focus:ring-0"
                        placeholder={t('contact.form.namePlaceholder')}
                        required // Zorunlu alan ekleyelim
                      />
                    </div>
                  </div>

                  <div className="lg:col-span-6">
                    <div className="text-start">
                      <label htmlFor="email" className="font-medium">
                        {t('contact.form.emailLabel')}
                      </label>
                      <input
                        name="entry.767018133" // Google Form ID'si
                        id="email"
                        type="email"
                        className="form-input mt-1 w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 focus:border-red-500 dark:border-gray-800 dark:focus:border-red-500 focus:ring-0"
                        placeholder={t('contact.form.emailPlaceholder')}
                        required // Zorunlu alan ekleyelim
                      />
                    </div>
                  </div>

                  <div className="lg:col-span-12">
                    <div className="text-start">
                      <label htmlFor="subject" className="font-medium">
                        {t('contact.form.subjectLabel')}
                      </label>
                      <input
                        name="entry.613116884" // Google Form ID'si
                        id="subject"
                        className="form-input mt-1 w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 focus:border-red-500 dark:border-gray-800 dark:focus:border-red-500 focus:ring-0"
                        placeholder={t('contact.form.subjectPlaceholder')}
                      />
                    </div>
                  </div>

                  <div className="lg:col-span-12">
                    <div className="text-start">
                      <label htmlFor="comments" className="font-medium">
                        {t('contact.form.messageLabel')}
                      </label>
                      <textarea
                        name="entry.2123576857" // Google Form ID'si
                        id="comments"
                        className="form-input mt-1 w-full py-2 px-3 h-28 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 focus:border-red-500 dark:border-gray-800 dark:focus:border-red-500 focus:ring-0"
                        placeholder={t('contact.form.messagePlaceholder')}
                        required // Zorunlu alan ekleyelim
                      ></textarea>
                    </div>
                  </div>

                  {/* Google Forms'un ihtiyaç duyduğu gizli alanları koruduk */}
                  <input type="hidden" name="fvv" value="1" />
                  <input type="hidden" name="fbzx" value="1102075907737746467" />

                  <div className="lg:col-span-12">
                    {/* Buton tipi artık onSubmit'i tetikleyeceği için "submit" olmalıdır */}
                    <button
                      type="submit"
                      id="submit"
                      name="send"
                      className="py-2 px-5 tracking-wider inline-flex items-center justify-center font-medium rounded bg-red-500 text-white"
                    >
                      {t('contact.form.submitButton')}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
